/**
 * Sticky title & price bar for the default product template.
 *
 * - Appears once the main product price has scrolled out of view, and hides
 *   again when the shopper scrolls back up to it or past the product section.
 * - Mirrors whatever the main price block is showing (via MutationObserver),
 *   so it stays in sync with variant changes and any script that edits the price.
 * - Also mirrors the Preferred Payment Price (clicking it runs the same checkout
 *   as the original button) and shows Affirm monthly-payment messaging that is
 *   refreshed with the current price.
 * - Shows the estimated gold weight on the variant line ("10K Yellow Gold · 14" · ≈11.0g").
 * - "Change options" scrolls the shopper back up to the start of the variant picker.
 * - Can mirror the Redeemly "Earn $X store credits" message if a credits element is
 *   added back to the bar (currently left out to keep the bar short).
 * - Is moved to <body> so no parent stacking context can clip or cover it.
 */
class ProductScrollSummary extends HTMLElement {
  connectedCallback() {
    // Moving the element to <body> fires connectedCallback again; ignore that.
    if (this.initialized) return;
    this.initialized = true;

    this.sectionId = this.dataset.sectionId;
    this.priceTarget = this.querySelector('[data-scroll-summary-price]');
    this.variantTarget = this.querySelector('[data-scroll-summary-variant]');
    this.weightTarget = this.querySelector('[data-scroll-summary-weight]');
    this.changeButton = this.querySelector('[data-scroll-summary-change]');
    this.changeButton?.addEventListener('click', () => this.scrollToOptions());
    if (this.variantTarget) this.variantTarget.textContent = this.formatVariantTitle(this.variantTarget.textContent);
    this.preferredButton = this.querySelector('[data-scroll-summary-preferred]');
    this.preferredAmount = this.querySelector('[data-scroll-summary-preferred-amount]');
    this.preferredBadge = this.querySelector('[data-scroll-summary-preferred-badge]');
    this.affirmTarget = this.querySelector('[data-scroll-summary-affirm]');
    this.creditsTarget = this.querySelector('[data-scroll-summary-credits]');
    this.innerEl = this.querySelector('.product-scroll-summary__inner');
    this.closeButton = this.querySelector('[data-scroll-summary-close]');
    this.restoreButton = this.querySelector('[data-scroll-summary-restore]');
    this.collapsed = this.readCollapsed();
    this.atcButton = this.querySelector('[data-scroll-summary-atc]');
    this.atcLabel = this.querySelector('[data-scroll-summary-atc-label]');
    this.atcDefaultText = this.atcLabel?.textContent || '';
    this.atcButton?.addEventListener('click', () => this.handleAddToCart());
    this.onCartDone = (event) => this.handleCartDone(event);
    document.addEventListener('cart:update', this.onCartDone);
    document.addEventListener('cart:error', this.onCartDone);

    // Shopper can hide the bar; the choice is remembered in this browser.
    this.closeButton?.addEventListener('click', () => this.setCollapsed(true));
    this.restoreButton?.addEventListener('click', () => this.setCollapsed(false));

    this.preferredButton?.addEventListener('click', () => {
      // Hand off to the original button so the existing preferred-payment checkout runs.
      this.observedPrice?.querySelector('[data-preferred-payment-checkout]')?.click();
    });

    // Theme editor re-renders can leave an old copy in <body>; remove it.
    document.querySelectorAll('body > product-scroll-summary').forEach((el) => {
      if (el !== this && el.id === this.id) el.remove();
    });

    if (this.parentElement !== document.body) document.body.appendChild(this);

    this.onScroll = () => this.requestUpdate();
    this.onVariantUpdate = (event) => this.handleVariantUpdate(event);

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    document.addEventListener('variant:update', this.onVariantUpdate);

    this.watchSourcePrice();
    this.syncPrice();
    this.watchCredits();
    this.requestUpdate();

    // Affirm's script may finish loading after this element; refresh once it has.
    if (document.readyState === 'complete') this.refreshAffirm();
    else window.addEventListener('load', () => this.refreshAffirm(), { once: true });
  }

  disconnectedCallback() {
    // Only tear down if genuinely removed (not just moved to <body>).
    if (this.isConnected) return;
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    document.removeEventListener('variant:update', this.onVariantUpdate);
    document.removeEventListener('cart:update', this.onCartDone);
    document.removeEventListener('cart:error', this.onCartDone);
    clearTimeout(this.atcTimer);
    this.priceObserver?.disconnect();
    this.creditsObserver?.disconnect();
    clearTimeout(this.creditsTimer);
    this.observedPrice = null;
    this.observedCredits = null;
    this.initialized = false;
  }

  get section() {
    return document.getElementById(`shopify-section-${this.sectionId}`);
  }

  /** The main price block in the product details column. */
  getSourcePrice() {
    const section = this.section;
    if (!section) return null;
    return (
      section.querySelector('.product-sticky-summary product-price') ||
      section.querySelector('.product-details product-price') ||
      section.querySelector('product-price')
    );
  }

  watchSourcePrice() {
    const source = this.getSourcePrice();
    if (source === this.observedPrice) return;

    this.priceObserver?.disconnect();
    this.observedPrice = source;
    if (!source) return;

    this.priceObserver = new MutationObserver(() => this.syncPrice());
    this.priceObserver.observe(source, { childList: true, subtree: true, characterData: true });
  }

  syncPrice() {
    const container = this.observedPrice?.querySelector('[ref="priceContainer"]');
    if (!container || !this.priceTarget) return;

    const clone = /** @type {HTMLElement} */ (container.cloneNode(true));
    clone
      .querySelectorAll('[data-preferred-payment-price], .unit-price, .installments, .volume-pricing-note, .tax-note')
      .forEach((el) => el.remove());
    // The weight goes on the variant line instead, without repeating karat and length.
    this.syncWeight(clone);
    clone.querySelectorAll('.mtj-weight').forEach((el) => el.remove());
    clone.removeAttribute('ref');
    clone.querySelectorAll('[ref], [id]').forEach((el) => {
      el.removeAttribute('ref');
      el.removeAttribute('id');
    });

    const html = clone.outerHTML;
    if (html !== this.lastPriceHtml) {
      this.lastPriceHtml = html;
      this.priceTarget.innerHTML = html;
    }

    this.syncPreferred(container);
    this.syncAffirm(container);
    this.syncAddToCart();
  }

  /** "10K Yellow Gold / 14 inches / Lobster Lock" → "10K Yellow Gold · 14" · Lobster Lock" */
  formatVariantTitle(title) {
    return String(title || '')
      .trim()
      .split(/\s+[\/·]\s+/)
      .map((part) => part.replace(/^(\d+(?:\.\d+)?)\s*(?:inches|inch)$/i, '$1"'))
      .join(' · ');
  }

  /** Copy just the "≈11.0g" (and "≈1.2ctw" for stones) from the price block's weight estimate. */
  syncWeight(container) {
    if (!this.weightTarget) return;
    const parts = [container.querySelector('[data-mtj-weight] .mtj-weight__text strong'), container.querySelector('[data-mtj-stones] .mtj-weight__text strong')]
      .map((el) => el?.textContent.trim())
      .filter(Boolean);
    const text = parts.join(' · ');
    if (this.weightTarget.textContent !== text) this.weightTarget.textContent = text;
    this.weightTarget.hidden = !text;
  }

  /** The first set of product options (e.g. Gold Purity and Color). */
  getVariantPicker() {
    return this.section?.querySelector('variant-picker') || null;
  }

  /**
   * Scroll back up so the start of the options sits just below the site header
   * and this bar (which stays open while the price is out of view).
   */
  scrollToOptions() {
    const picker = this.getVariantPicker();
    if (!picker) return;

    const header = document.querySelector('#header-component');
    const headerHeight = Math.max(this.getHeaderBottom(), header?.offsetHeight || 0);
    const barHeight = this.collapsed ? 0 : this.offsetHeight || 0;
    const top = picker.getBoundingClientRect().top + window.scrollY - headerHeight - barHeight - 12;
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: Math.max(0, top), behavior: smooth ? 'smooth' : 'auto' });

    // Move keyboard focus to the selected option without a second jump.
    const first = picker.querySelector('input:checked, select, input, button');
    first?.focus({ preventScroll: true });
  }

  /** The product page's own Add to cart button (inside the buy buttons block). */
  getMainAddToCart() {
    return this.section?.querySelector('product-form-component [ref="addToCartButton"]') || null;
  }

  /** Show the bar's button only when the main button exists, is shown and can be used. */
  syncAddToCart() {
    if (!this.atcButton) return;
    const main = this.getMainAddToCart();
    const usable = Boolean(main && !main.disabled && main.getClientRects().length);
    this.atcButton.hidden = !usable;
  }

  /**
   * Press the main Add to cart button so everything the product form does
   * (selected variant, nameplate and other custom fields, cart drawer) still applies.
   * If the form still needs input, scroll the shopper back up to it instead.
   */
  handleAddToCart() {
    const main = this.getMainAddToCart();
    if (!main || main.disabled) {
      this.syncAddToCart();
      return;
    }

    const form = main.closest('form');
    if (form && typeof form.checkValidity === 'function' && !form.checkValidity()) {
      main.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => form.reportValidity(), 400);
      return;
    }

    this.atcButton.setAttribute('aria-busy', 'true');
    clearTimeout(this.atcTimer);
    // Safety net in case no cart event comes back.
    this.atcTimer = setTimeout(() => this.resetAddToCart(), 6000);
    // Skip the main button's own fly-to-cart animation; it starts from off-screen.
    main.dataset.puppet = 'true';
    main.click();
  }

  handleCartDone(event) {
    if (this.atcButton?.getAttribute('aria-busy') !== 'true') return;
    const main = this.getMainAddToCart();
    if (main) main.dataset.puppet = 'false';
    clearTimeout(this.atcTimer);
    this.atcButton.removeAttribute('aria-busy');

    if (event.type === 'cart:error') {
      // Let the main form show its message.
      this.getMainAddToCart()?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this.resetAddToCart();
      return;
    }

    if (this.atcLabel) this.atcLabel.textContent = 'Added ✓';
    this.atcTimer = setTimeout(() => this.resetAddToCart(), 1800);
  }

  resetAddToCart() {
    this.atcButton?.removeAttribute('aria-busy');
    const main = this.getMainAddToCart();
    if (main?.dataset.puppet === 'true') main.dataset.puppet = 'false';
    if (this.atcLabel) this.atcLabel.textContent = this.atcDefaultText;
  }

  /** Copy the Preferred Payment Price amount and badge from the main price block. */
  syncPreferred(container) {
    if (!this.preferredButton) return;
    const source = container.querySelector('[data-preferred-payment-price]');
    const amount = source?.querySelector('.preferred-payment-price__amount strong')?.textContent.trim();

    if (!amount) {
      this.preferredButton.hidden = true;
      return;
    }

    this.preferredButton.hidden = false;
    if (this.preferredAmount.textContent !== amount) this.preferredAmount.textContent = amount;
    const badge = source.querySelector('.preferred-payment-price__badge')?.textContent.trim() || '';
    if (this.preferredBadge.textContent !== badge) this.preferredBadge.textContent = badge;
  }

  /** Read the headline selling price (in cents) from the main price block. */
  getPriceCents(container) {
    const priceEl =
      container.querySelector('.price__sale .price-item--sale') ||
      container.querySelector('.price__regular .price') ||
      container.querySelector('.price');
    const match = priceEl?.textContent.match(/\d[\d,]*(\.\d{1,2})?/);
    if (!match) return 0;
    const value = parseFloat(match[0].replace(/,/g, ''));
    return Number.isFinite(value) ? Math.round(value * 100) : 0;
  }

  /** Keep the Affirm "as low as" message in step with the selected variant's price. */
  syncAffirm(container) {
    if (!this.affirmTarget) return;
    const cents = this.getPriceCents(container);

    if (!cents) {
      this.affirmTarget.hidden = true;
      return;
    }

    this.affirmTarget.hidden = false;
    if (String(cents) === this.affirmTarget.dataset.amount && this.affirmRendered) return;
    this.affirmTarget.dataset.amount = String(cents);
    this.refreshAffirm();
  }

  /** Redeemly's "Earn $X store credits" widget in the product details column. */
  getSourceCredits() {
    return this.section?.querySelector('#mcb__rewards-on-pdp-widget__root') || null;
  }

  /**
   * Redeemly fills its widget in after page load and rewrites it when the variant
   * changes, so watch it and copy across whatever it currently says.
   */
  watchCredits(attempt = 0) {
    if (!this.creditsTarget) return;
    const source = this.getSourceCredits();

    if (!source) {
      // The app block may not be on the page yet; look again for a few seconds.
      clearTimeout(this.creditsTimer);
      if (attempt < 20) this.creditsTimer = setTimeout(() => this.watchCredits(attempt + 1), 500);
      this.syncCredits();
      return;
    }

    if (source !== this.observedCredits) {
      this.creditsObserver?.disconnect();
      this.observedCredits = source;
      this.creditsObserver = new MutationObserver(() => this.syncCredits());
      this.creditsObserver.observe(source, { childList: true, subtree: true, characterData: true });
    }

    this.syncCredits();
  }

  syncCredits() {
    if (!this.creditsTarget) return;
    const widget = this.observedCredits?.isConnected ? this.observedCredits.querySelector('.mcb-pdp-widget') : null;
    // The message is the widget's first element; the info icon and tooltip follow it.
    const text = widget?.firstElementChild?.textContent.replace(/\s+/g, ' ').trim() || '';
    const match = text.match(/^(.*?)(\S*\d[\d.,]*\S*)(.*)$/);

    if (!match) {
      this.creditsTarget.hidden = true;
      this.lastCreditsText = '';
      return;
    }

    this.creditsTarget.hidden = false;
    if (text === this.lastCreditsText) return;
    this.lastCreditsText = text;

    const amount = document.createElement('strong');
    amount.textContent = match[2];
    this.creditsTarget.replaceChildren(match[1], amount, match[3]);
  }

  refreshAffirm() {
    const affirm = window.affirm;
    if (!affirm?.ui) return;
    clearTimeout(this.affirmTimer);
    // Debounce so quick variant clicks only trigger one refresh.
    this.affirmTimer = setTimeout(() => {
      const run = () => {
        try {
          affirm.ui.refresh();
          this.affirmRendered = true;
        } catch (error) {
          // Affirm unavailable (e.g. blocked or outside supported region); leave the message out.
        }
      };
      typeof affirm.ui.ready === 'function' ? affirm.ui.ready(run) : run();
    }, 150);
  }

  handleVariantUpdate(event) {
    const section = this.section;
    if (!section || !(event.target instanceof Node) || !section.contains(event.target)) return;

    const title = event.detail?.resource?.title;
    if (this.variantTarget && typeof title === 'string') {
      this.variantTarget.textContent = this.formatVariantTitle(title);
    }

    // The price block updates itself on this same event; sync after it has run.
    requestAnimationFrame(() => {
      this.watchSourcePrice();
      this.syncPrice();
      this.watchCredits();
      this.requestUpdate();
    });
  }

  readCollapsed() {
    try {
      return window.localStorage.getItem('mtj-sticky-price-hidden') === '1';
    } catch (error) {
      return false;
    }
  }

  setCollapsed(collapsed) {
    this.collapsed = collapsed;
    try {
      if (collapsed) window.localStorage.setItem('mtj-sticky-price-hidden', '1');
      else window.localStorage.removeItem('mtj-sticky-price-hidden');
    } catch (error) {
      // Storage unavailable (private mode etc.); the choice lasts for this page only.
    }
    this.updateVisibility();
    // Keep keyboard focus on the control that just appeared.
    (collapsed ? this.restoreButton : this.closeButton)?.focus({ preventScroll: true });
  }

  requestUpdate() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.updateVisibility();
    });
  }

  /** Bottom edge of the sticky header, or 0 if the header has scrolled away. */
  getHeaderBottom() {
    const header = document.querySelector('#header-component');
    if (!header) return 0;
    const rect = header.getBoundingClientRect();
    return rect.bottom > 0 ? Math.round(rect.bottom) : 0;
  }

  updateVisibility() {
    if (!this.observedPrice?.isConnected) this.watchSourcePrice();

    const price = this.observedPrice;
    const section = this.section;
    if (!price || !section) {
      this.toggleAttribute('data-visible', false);
      this.toggleAttribute('data-collapsed', false);
      this.inert = true;
      return;
    }

    const headerBottom = this.getHeaderBottom();
    this.style.setProperty('--scroll-summary-top', `${headerBottom}px`);

    // Measure the headline price only (not the payment card beneath it).
    const headline = price.querySelector('.price__regular, .price__sale, .price-item__group') || price;
    const priceBottom = headline.getBoundingClientRect().bottom;
    const sectionBottom = section.getBoundingClientRect().bottom;
    const barHeight = this.offsetHeight || 60;

    const priceScrolledPast = priceBottom <= headerBottom;
    const stillInProduct = sectionBottom > headerBottom + barHeight;

    const visible = priceScrolledPast && stillInProduct;
    const collapsed = Boolean(this.collapsed);
    this.toggleAttribute('data-visible', visible && !collapsed);
    // Hidden by the shopper: show only the small "Show price" tab.
    this.toggleAttribute('data-collapsed', visible && collapsed);
    // Keep the hidden bar out of the tab order and away from screen readers.
    this.inert = !visible;
    if (this.innerEl) this.innerEl.inert = collapsed;
    this.restoreButton?.setAttribute('aria-expanded', String(!collapsed));
  }
}

if (!customElements.get('product-scroll-summary')) {
  customElements.define('product-scroll-summary', ProductScrollSummary);
}
